import React, { useEffect, useState } from "react";
import { Box, TextField, Typography, IconButton, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface ManualModelSectionProps {
  baselineDailySummaryData: any;
  manualModelDetails: any;
  setManualModelDetails: React.Dispatch<React.SetStateAction<any>>;
  onValidationChange: (isValid: boolean) => void;
  energyModel: string;
}

const ManualModelDetails: React.FC<ManualModelSectionProps> = ({ baselineDailySummaryData, manualModelDetails, setManualModelDetails, onValidationChange, energyModel = "cooling" }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [inputValues, setInputValues] = useState<any>({});

  const handleToggleExpand = () => setIsExpanded((prev) => !prev);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    const numericRegex = /^-?\d*\.?\d*$/;

    if (numericRegex.test(value) || value === "" || value === "-" || value === ".") {
      // Always update string input (for UI typing)
      setInputValues((prev: any) => ({
        ...prev,
        [name]: value,
      }));

      // If input is empty or incomplete, clear the numeric model value
      if (value === "" || value === "-" || value === ".") {
        setManualModelDetails((prev: any) => ({
          ...prev,
          [name]: undefined, // <-- clears it, so validation fails
        }));
        return;
      }

      // Otherwise, parse to number
      const parsedValue = Number(value);
      if (!isNaN(parsedValue)) {
        setManualModelDetails((prev: any) => ({
          ...prev,
          [name]: parsedValue,
        }));
      }
    }
  };

  const isNumberValid = (val: any) => typeof val === "number" && Number.isFinite(val);

  useEffect(() => {
    if (energyModel === "cooling") {
      const isValid = isNumberValid(manualModelDetails?.cc) && isNumberValid(manualModelDetails?.bc);
      onValidationChange(isValid);
    } else {
      const isValid = isNumberValid(manualModelDetails?.ch) && isNumberValid(manualModelDetails?.bh);
      onValidationChange(isValid);
    }
  }, [manualModelDetails, energyModel, onValidationChange]);

  const renderTextField = (label: string, name: string, value: string | number, required: boolean = false) => (
    <TextField
      fullWidth
      label={label}
      name={name}
      variant="outlined"
      margin="dense"
      value={inputValues[name] ?? value ?? ""}
      onChange={handleInputChange}
      required={required}
      slotProps={{
        input: {
          inputMode: "decimal",
          inputProps: {
            pattern: "-?[0-9]*\\.?[0-9]*",
          },
        },
      }}
    />
  );

  // console.log("Baseline Daily Summary Data suggested_values:", baselineDailySummaryData?.suggested_values);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <Typography variant="h6">Manual Model Details</Typography>
        <IconButton onClick={handleToggleExpand} size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ gap: 2, marginTop: 2, marginBottom: 2 }}>
          {renderTextField(energyModel === "cooling" ? "Cc" : "Ch", energyModel === "cooling" ? "cc" : "ch", energyModel === "cooling" ? manualModelDetails?.cc : manualModelDetails?.ch, true)}
          <Typography sx={{ fontSize: "12px", color: "text.secondary", marginLeft: "14px", marginBottom: "10px" }}>
            Suggested {energyModel === "cooling" ? "Cc" : "Ch"}:{" "}
            {energyModel === "cooling" ? baselineDailySummaryData?.suggested_values?.cooling?.cc || 0 : baselineDailySummaryData?.suggested_values?.heating?.ch || 0}
          </Typography>
          {renderTextField(energyModel === "cooling" ? "Bc" : "Bh", energyModel === "cooling" ? "bc" : "bh", energyModel === "cooling" ? manualModelDetails?.bc : manualModelDetails?.bh, true)}
          <Typography sx={{ fontSize: "12px", color: "text.secondary", marginLeft: "14px", marginBottom: "10px" }}>
            Suggested {energyModel === "cooling" ? "Bc" : "Bh"}:{" "}
            {energyModel === "cooling" ? baselineDailySummaryData?.suggested_values?.cooling?.bc || 0 : baselineDailySummaryData?.suggested_values?.heating?.bh || 0}
          </Typography>
          {renderTextField(
            energyModel === "cooling" ? "TCCP" : "THCP",
            energyModel === "cooling" ? "tccp" : "thcp",
            energyModel === "cooling" ? manualModelDetails?.tccp : manualModelDetails?.thcp,
            false
          )}
          <Typography sx={{ fontSize: "12px", color: "text.secondary", marginLeft: "14px", marginBottom: "10px" }}>
            Suggested {energyModel === "cooling" ? "TCCP" : "THCP"}:{" "}
            {energyModel === "cooling" ? baselineDailySummaryData?.suggested_values?.cooling?.tcp || 0 : baselineDailySummaryData?.suggested_values?.heating?.thcp || 0}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ManualModelDetails;
